import { all, call, put, select, take, takeEvery, takeLatest } from 'redux-saga/effects';

import { orderCancelErrorMessage, orderDocument } from '@elab/core/utils/constants';
import { selectors as configSelectors } from '@elab/core/store/config';
import { deleteCookie } from '@elab/core/utils/cookie';
import { actions as uiActions } from '@elab/core/store/ui';

import { types as authTypes } from '../auth';
import { selectors as userSelectors, types as userTypes } from '../user';
import { actions as cartActions, selectors as cartSelectors } from '../cart';
import { actions as globalActions, types as globalTypes } from '../global';
import authCookiesManager from '../../utils/authCookiesManager';
import logger from '../../utils/logger';

import { errorsToRedirect, errorsToStop } from './sagaHelpers';
import api from './api';

import { actions, selectors, types } from '.';

export function* loadOrderHandler(code, userId, isFull, isOffline) {
    if (yield select(selectors.isLoading)) return;
    yield put(actions.clearOrder());
    yield put(actions.setLoading(true));
    yield put(actions.setDataType(code, isFull));

    try {
        const response = isOffline
            ? yield call(api.loadOfflineOrder, code, userId, isFull)
            : yield call(api.loadOrder, code, userId, isFull);

        if (response.amountToPay) {
            response.isInvoiceDisabled = response.documents
                ? !response.documents.filter(item => item.label.includes('invoiceDocument'))
                      .length > 0
                : true;
            const cancelStatus =
                response.status === 'CHECKED_INVALID' || response.status === 'CREATED';
            const disableCancelButton = !response.userCancellationAllowed && cancelStatus;
            const hideCancelButton = !response.userCancellationAllowed && !cancelStatus;
            yield put(actions.setOrder({ ...response, disableCancelButton, hideCancelButton }));
        }
    } catch (e) {
        yield call(logger.error, e);
    }

    yield put(actions.setLoading(false));
}

export function* loadOrder({ payload }) {
    const { code, isFull } = yield select(selectors.getDataType);

    if (payload.code === code && payload.isFull === isFull && !payload.isResendEGiftEmail) {
        return;
    }

    let userId = yield select(userSelectors.getUserId);
    const isAuthorizedInCookies = yield call(authCookiesManager.isAuthorized);

    if (!userId && isAuthorizedInCookies) {
        yield take(userTypes.SET_PROFILE);
        userId = yield select(userSelectors.getUserId);
    }

    yield call(loadOrderHandler, payload.code, userId, payload.isFull, payload.isOffline);

    const paymentAvailable = yield select(cartSelectors.isPaymentAvailable);
    if (!paymentAvailable) {
        const giftCards = yield select(cartSelectors.getGiftCardTransactions);
        const couponDetails = yield select(cartSelectors.getCouponDetails);

        if (giftCards.length) {
            yield all([put(cartActions.setCode()), put(cartActions.setGuid())]);
            yield put(cartActions.loadCart());
        } else if (couponDetails.length) {
            yield put(cartActions.loadCart());
        }
    }

    const setAnalyticsCookies = yield select(configSelectors.setAnalyticsCookie);
    if (setAnalyticsCookies) {
        yield call(deleteCookie, 'analyticsData');
    }
}

export function* logout() {
    yield put(actions.clearOrder());
}

export function* repeatOrder({ payload }) {
    const userId = yield select(userSelectors.getUserId);
    const cartId = yield select(cartSelectors.getCartId);

    try {
        const addedProducts = yield call(api.repeatOrder, payload.orderCode, userId, cartId);

        const statusCode = addedProducts.statusCode;

        if (errorsToRedirect.includes(statusCode)) {
            yield put(globalActions.redirect(`/repeatCart?statusCode=${statusCode}`));
            yield take(globalTypes.PAGE_LOAD);
            return;
        }

        if (errorsToStop.includes(statusCode)) {
            yield put(actions.setCurrentErrorCode(statusCode));
            return;
        }

        yield put(globalActions.redirect(payload));
        yield take(globalTypes.PAGE_LOAD);
    } catch (e) {
        yield call(logger.error, e);
    }
}

export function* downloadInvoice() {
    const flag = yield select(configSelectors.getDownloadEnable); // need this flag, so that we can check content length before downloading
    let downloadLocations = yield select(selectors.getDocuments);

    if (downloadLocations && !flag) {
        const invoiceDownloadDetail = downloadLocations.filter(item =>
            item.label.includes(orderDocument.INVOICE_DOCUMENT),
        );
        if (invoiceDownloadDetail[0] && invoiceDownloadDetail[0].downloadLink) {
            yield put(globalActions.goto(invoiceDownloadDetail[0].downloadLink, true));
        }
    } else {
        const orderGuid = yield select(selectors.getOrderGUID);
        let contentLength = 0;
        yield api
            .downloadInvoiceFromUrl(orderGuid)
            .then(response => {
                contentLength = response.headers.get('Content-Length');
                return response.blob();
            })
            .then(blob => {
                if (Number(contentLength) > 0) {
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `invoice-${orderGuid}.pdf`;
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    return blob;
                }
            });
        if (Number(contentLength) <= 0) {
            yield put(uiActions.openDialog({ name: 'myAccount-invoice-error' }));
        }
    }
}

export function* trackOrder() {
    const trackingUrls = yield select(selectors.getOrderTrackingUrls);
    if (trackingUrls[0]) {
        yield put(globalActions.openURL(trackingUrls[0], true));
    }
}

export function* cancelOrder() {
    const code = yield select(selectors.getOrderCode);
    const userId = yield select(userSelectors.getUserId);
    try {
        const response = yield call(api.cancelOrder, userId, code);
        if (response) {
            // yield put(globalActions.addMessage(orderCancelSuccessMessage));
            location.reload();
        }
    } catch (err) {
        yield put(uiActions.closeDialog());
        yield put(globalActions.addMessage(orderCancelErrorMessage));
    }
}

export function* openCancelDialog() {
    yield put(uiActions.openDialog({ name: 'cancel-order-confirmation' }));
}

export default [
    takeEvery(types.LOAD_ORDER, loadOrder),
    takeEvery(authTypes.DO_LOGOUT, logout),
    takeLatest(types.REPEAT_ORDER, repeatOrder),
    takeLatest(types.DOWNLOAD_INVOICE, downloadInvoice),
    takeEvery(types.TRACK_ORDER, trackOrder),
    takeLatest(types.OPEN_CANCEL_DIALOG, openCancelDialog),
    takeLatest(types.CANCEL_ORDER, cancelOrder),
];
